import { Button, typeSize } from "@coaching-culture/ui";
import { useUser } from "auth";
import {
  addDays,
  addWeeks,
  format,
  fromUnixTime,
  isSameDay,
  isToday,
  startOfToday,
  startOfWeek,
} from "date-fns";
import { sortBy } from "lodash";
import { useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import styled from "styled-components";
import { useConversationActions } from "./ConversationActions";
import {
  ConvoAndInstance,
  detailsHref,
  isCancelled,
  isCompleted,
  isUpcoming,
  playHref,
  recurrenceLabel,
} from "./conversationList";

const weekStartOf = (d: Date) => startOfWeek(d, { weekStartsOn: 1 });

const Nav = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;

  & > h2 {
    margin: 0 0 0 8px;
    font-size: ${typeSize("section")};
    font-weight: 700;
    color: ${(p) => p.theme.colors.black};
  }
`;

const NavButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.colors.borderStrong};
  background: ${(p) => p.theme.colors.surface};
  color: ${(p) => p.theme.colors.black};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const Layout = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const GridBox = styled.div`
  flex: 999 1 780px;
  min-width: 0;
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
  overflow-x: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(88px, 1fr));
  min-width: 616px;
`;

const Day = styled.div<{ past: boolean }>`
  min-width: 0;
  border-right: 1px solid ${(p) => p.theme.colors.grey5};
  background: ${(p) => (p.past ? p.theme.colors.grey7 : "transparent")};

  &:last-child {
    border-right: 0;
  }
`;

const DayHead = styled.div<{ today: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 12px 6px 10px;
  border-bottom: 1px solid ${(p) => p.theme.colors.grey5};

  & > span:first-child {
    font-size: ${typeSize("caption")};
    font-weight: 600;
    color: ${(p) => p.theme.colors.textMuted};
    text-transform: uppercase;
  }

  & > span:last-child {
    width: 34px;
    height: 34px;
    border-radius: 17px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${typeSize("title")};
    font-weight: 700;
    background: ${(p) => (p.today ? p.theme.colors.primary : "transparent")};
    color: ${(p) => (p.today ? p.theme.colors.surface : p.theme.colors.black)};
  }
`;

const DayBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 6px;
  min-height: 360px;
`;

const Chip = styled.button<{ selected: boolean; cancelled: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  font-family: inherit;
  cursor: pointer;
  background: ${(p) =>
    p.cancelled ? p.theme.colors.grey6 : p.theme.colors.conversationsSoft};
  border: 2px solid
    ${(p) => (p.selected ? p.theme.colors.primary : "transparent")};
  text-decoration: ${(p) => (p.cancelled ? "line-through" : "none")};
  overflow-wrap: break-word;
  hyphens: auto;

  & > span:nth-child(1) {
    font-size: ${typeSize("caption")};
    font-weight: 600;
    color: ${(p) => p.theme.colors.textMuted};
  }

  & > span:nth-child(2) {
    font-size: ${typeSize("small")};
    font-weight: 700;
    color: ${(p) => p.theme.colors.black};
    line-height: 1.3;
  }

  & > span:nth-child(3) {
    font-size: ${typeSize("caption")};
    color: ${(p) => p.theme.colors.body};
  }

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const Detail = styled.aside`
  flex: 1 1 320px;
  min-width: 0;
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: ${typeSize("small")};
  color: ${(p) => p.theme.colors.textMuted};
  line-height: 1.5;
`;

const Eyebrow = styled.span`
  font-size: ${typeSize("caption")};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.textMuted};
`;

const DetailTitle = styled.h3`
  margin: 0;
  font-size: ${typeSize("page")};
  font-weight: 700;
  color: ${(p) => p.theme.colors.black};
`;

const Facts = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 14px;

  & > dd {
    margin: 0;
    color: ${(p) => p.theme.colors.black};
  }
`;

const AgendaList = styled.ol`
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: ${typeSize("body")};
  color: ${(p) => p.theme.colors.black};
`;

const Note = styled.p`
  margin: 0;
  padding: 12px 14px;
  border-radius: 8px;
  background: ${(p) => p.theme.colors.warningSoft};
  color: ${(p) => p.theme.colors.warningText};
`;

const key = (x: ConvoAndInstance) => `${x.id}-${x.index}`;

export type WeekViewProps = {
  instances: ConvoAndInstance[];
};

export const WeekView = ({ instances }: WeekViewProps) => {
  const [user] = useUser();
  const [weekStart, setWeekStart] = useState(() => weekStartOf(new Date()));
  const [selected, setSelected] = useState<string | null>(null);

  const days = [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 7);
  const inWeek = sortBy(
    instances.filter((x) => {
      const d = fromUnixTime(x.scheduledDate);
      return d >= weekStart && d < weekEnd;
    }),
    (x) => x.scheduledDate,
  );

  const current =
    inWeek.find((x) => key(x) === selected) ??
    inWeek.find((x) => isUpcoming(x) && !isCancelled(x)) ??
    inWeek[0] ??
    null;

  const goTo = (d: Date) => {
    setWeekStart(d);
    setSelected(null);
  };

  const label = `${format(weekStart, "d MMM")} – ${format(
    addDays(weekStart, 6),
    "d MMM yyyy",
  )}`;

  return (
    <>
      <Nav>
        <NavButton
          type="button"
          aria-label="Previous week"
          onClick={() => goTo(addWeeks(weekStart, -1))}
        >
          <FaChevronLeft aria-hidden />
        </NavButton>
        <NavButton
          type="button"
          aria-label="Next week"
          onClick={() => goTo(addWeeks(weekStart, 1))}
        >
          <FaChevronRight aria-hidden />
        </NavButton>
        <Button small onClick={() => goTo(weekStartOf(new Date()))}>
          This week
        </Button>
        <h2>{label}</h2>
      </Nav>
      <Layout>
        <GridBox>
          <Grid>
            {days.map((d) => (
              <Day key={d.toISOString()} past={d < startOfToday()}>
                <DayHead today={isToday(d)}>
                  <span>{format(d, "EEE")}</span>
                  <span>{format(d, "d")}</span>
                </DayHead>
                <DayBody>
                  {inWeek
                    .filter((x) => isSameDay(fromUnixTime(x.scheduledDate), d))
                    .map((x) => {
                      const other =
                        x.manager.id === user.id ? x.directReport : x.manager;
                      return (
                        <Chip
                          key={key(x)}
                          type="button"
                          selected={current != null && key(current) === key(x)}
                          cancelled={isCancelled(x)}
                          aria-pressed={
                            current != null && key(current) === key(x)
                          }
                          onClick={() => setSelected(key(x))}
                        >
                          <span>
                            {format(fromUnixTime(x.scheduledDate), "HH:mm")}
                          </span>
                          <span>{x.type}</span>
                          <span>{other.name}</span>
                        </Chip>
                      );
                    })}
                </DayBody>
              </Day>
            ))}
          </Grid>
        </GridBox>
        <Detail aria-label="Selected conversation">
          {current ? (
            <SelectedConversation key={key(current)} convo={current} />
          ) : (
            <span>
              Nothing booked this week. Pick another week, or schedule a
              conversation.
            </span>
          )}
        </Detail>
      </Layout>
    </>
  );
};

const SelectedConversation = ({ convo }: { convo: ConvoAndInstance }) => {
  const [user] = useUser();
  const actions = useConversationActions(convo);
  const date = fromUnixTime(convo.scheduledDate);
  const other =
    convo.manager.id === user.id ? convo.directReport : convo.manager;
  const repeat = recurrenceLabel(convo.recurrence);
  const agenda = convo.agenda ?? [];
  const cancelled = isCancelled(convo);
  const completed = isCompleted(convo);
  const upcoming = isUpcoming(convo);

  return (
    <>
      {actions.modals}
      <div>
        <Eyebrow>
          {format(date, "EEEE d MMMM")}
          {cancelled ? " · Cancelled" : completed ? " · Completed" : ""}
        </Eyebrow>
        <DetailTitle>{convo.type}</DetailTitle>
      </div>
      <Facts>
        <dt>With</dt>
        <dd>
          {other.name}
          {convo.directReport.id === user.id ? " (your manager)" : ""}
        </dd>
        <dt>Time</dt>
        <dd>
          {format(date, "HH:mm")} · {convo.duration} min
        </dd>
        {repeat && (
          <>
            <dt>Repeats</dt>
            <dd>{repeat}</dd>
          </>
        )}
        <dt>Where</dt>
        <dd>{convo.location || "Not set"}</dd>
      </Facts>
      <div>
        <Eyebrow>Agenda</Eyebrow>
        {agenda.length > 0 ? (
          <AgendaList>
            {agenda.map((a) => (
              <li key={a.id}>{a.name}</li>
            ))}
          </AgendaList>
        ) : upcoming && !cancelled ? (
          <Note>No agenda yet. Add discussion points so you both come prepared.</Note>
        ) : (
          <div>No agenda points.</div>
        )}
      </div>
      {!cancelled && (
        <Button
          variant="primary"
          to={upcoming ? detailsHref(convo) : playHref(convo)}
        >
          {upcoming ? "Prepare" : "Open"}
        </Button>
      )}
      {(actions.canReschedule || actions.canCancel) && (
        <div style={{ display: "flex", gap: 8 }}>
          {actions.canReschedule && (
            <Button small style={{ flex: 1 }} onClick={actions.startReschedule}>
              Reschedule
            </Button>
          )}
          {actions.canCancel && (
            <Button
              small
              color="danger"
              style={{ flex: 1 }}
              onClick={actions.startCancel}
            >
              Cancel
            </Button>
          )}
        </div>
      )}
    </>
  );
};
